import { useEffect } from "react";
import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import { InstallWall } from "./components/InstallWall";
import { Splash } from "./components/Splash";

/**
 * Le schermate caricate solo quando servono.
 *
 * Tutta l'app stava in **un file solo da 975 KB**: aprendo Qota si scaricava
 * anche il modulo delle statistiche, quello degli amici e quello della
 * diagnostica prima di poter vedere la lista dei gruppi.
 *
 * Restano dentro al primo blocco solo le schermate che servono al primo
 * fotogramma — la lista dei gruppi, un gruppo, l'accoglienza — perché
 * spezzettare *quelle* aggiungerebbe un giro di rete proprio dove si sta
 * cercando di toglierlo.
 *
 * `lazyRouteComponent` e non `React.lazy`: il router sa precaricare il pezzo
 * mentre il dito è ancora sul link, invece di aspettare il tocco.
 */
const ActivityScreen = lazyRouteComponent(
  () => import("./screens/ActivityScreen"),
  "ActivityScreen",
);
const AddFriendScreen = lazyRouteComponent(
  () => import("./screens/AddFriendScreen"),
  "AddFriendScreen",
);
const FriendScreen = lazyRouteComponent(() => import("./screens/FriendScreen"), "FriendScreen");
const FriendsScreen = lazyRouteComponent(() => import("./screens/FriendsScreen"), "FriendsScreen");
const DebugScreen = lazyRouteComponent(() => import("./screens/DebugScreen"), "DebugScreen");
const NewExpenseScreen = lazyRouteComponent(
  () => import("./screens/NewExpenseScreen"),
  "NewExpenseScreen",
);
const GroupSettingsScreen = lazyRouteComponent(
  () => import("./screens/GroupSettingsScreen"),
  "GroupSettingsScreen",
);
const GroupExpensesScreen = lazyRouteComponent(
  () => import("./screens/GroupExpensesScreen"),
  "GroupExpensesScreen",
);
const GroupStatsScreen = lazyRouteComponent(
  () => import("./screens/GroupStatsScreen"),
  "GroupStatsScreen",
);
const GroupBalancesScreen = lazyRouteComponent(
  () => import("./screens/GroupBalancesScreen"),
  "GroupBalancesScreen",
);
const NewGroupScreen = lazyRouteComponent(
  () => import("./screens/NewGroupScreen"),
  "NewGroupScreen",
);
const ProfileScreen = lazyRouteComponent(() => import("./screens/ProfileScreen"), "ProfileScreen");
const EnterCodeScreen = lazyRouteComponent(
  () => import("./screens/EnterCodeScreen"),
  "EnterCodeScreen",
);
const InviteScreen = lazyRouteComponent(() => import("./screens/InviteScreen"), "InviteScreen");
const OnboardingScreen = lazyRouteComponent(
  () => import("./screens/OnboardingScreen"),
  "OnboardingScreen",
);
const VerifyCodeScreen = lazyRouteComponent(
  () => import("./screens/VerifyCodeScreen"),
  "VerifyCodeScreen",
);
const SignInScreen = lazyRouteComponent(() => import("./screens/SignInScreen"), "SignInScreen");
import { shouldRequireInstall } from "./lib/installed";
import { resetDirectionAfterNavigation } from "./lib/transitions";
import { readPendingInvite } from "./lib/invites";
import { SessionProvider, useSessionState } from "./lib/session-context";
import { GroupScreen } from "./screens/GroupScreen";
import { GroupsScreen } from "./screens/GroupsScreen";
import { WelcomeScreen } from "./screens/WelcomeScreen";

/**
 * Smistamento in ingresso.
 *
 * La sessione la stabilisce l'API, non il client: interroghiamo `/me` invece di
 * fidarci della presenza di un cookie, che potrebbe essere scaduto o revocato.
 *
 * Tre esiti: nessuna sessione → benvenuto; sessione senza nickname → il profilo
 * va completato; sessione completa → dentro.
 */
function IndexRoute() {
  const navigate = useNavigate();
  const { profile, loading, unauthenticated } = useSessionState();

  useEffect(() => {
    if (loading || unauthenticated) return;

    if (profile && !profile.nickname) {
      void navigate({ to: "/onboarding", replace: true });
      return;
    }

    if (!profile) return;

    // Chi è arrivato da un invito e ha fatto il giro dell'accesso torna al suo
    // invito, non alla home: altrimenti il link sarebbe stato inutile.
    //
    // QUI SI LEGGE SOLTANTO. A cancellare il codice è la schermata dell'invito,
    // quando è lei ad averlo in mano: questo effetto può girare più di una
    // volta — React lo esegue due volte al montaggio in sviluppo, e in ogni
    // caso ribatte a ogni cambio di `profile` — e consumando qui il codice la
    // seconda esecuzione non lo troverebbe più, cadrebbe nel ramo finale e
    // manderebbe l'utente sulla lista dei gruppi, annullando la navigazione
    // appena fatta. È esattamente così che un invito accettato finiva per non
    // essere mai accettato.
    const pending = readPendingInvite();
    if (pending) {
      void navigate({ to: "/i/$code", params: { code: pending }, replace: true });
      return;
    }

    void navigate({ to: "/gruppi", replace: true });
  }, [loading, unauthenticated, profile, navigate]);

  if (loading) return <Splash />;
  if (unauthenticated) return <WelcomeScreen />;

  return <Splash />;
}

/** Riporta al benvenuto chi arriva su una schermata protetta senza sessione. */
function RequireSession({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const { loading, unauthenticated } = useSessionState();

  useEffect(() => {
    if (!loading && unauthenticated) void navigate({ to: "/", replace: true });
  }, [loading, unauthenticated, navigate]);

  if (loading) return <Splash />;
  if (unauthenticated) return <Splash />;

  return <>{children}</>;
}

/**
 * Il guardiano dell'installazione.
 *
 * Su telefono, fuori dall'app installata, Qota non parte: in una scheda di
 * Safari **le notifiche push non esistono**, e metà di quello che l'app promette
 * non potrebbe funzionare.
 *
 * Sta **dentro** il provider di sessione ma **sopra** l'`Outlet`, perché la
 * decisione non dipende dalla sessione e non deve aspettarla: chi arriva da un
 * link va indirizzato subito, non dopo il giro su `/me`.
 *
 * Il controllo guarda il percorso corrente a ogni navigazione, così le rotte
 * esenti — l'invito, il codice, la diagnostica — restano raggiungibili anche
 * navigandoci da dentro.
 */
function RequireInstall({ children }: { children: React.ReactNode }) {
  const { pathname } = useRouterState({ select: (state) => state.location });

  return shouldRequireInstall(pathname) ? <InstallWall /> : <>{children}</>;
}

const rootRoute = createRootRoute({
  // Il provider sta qui e non dentro le singole rotte: lo stato di sessione
  // deve sopravvivere ai cambi di schermata, altrimenti ogni navigazione
  // rileggerebbe `/me` da capo.
  component: () => (
    <SessionProvider>
      <RequireInstall>
        <Outlet />
      </RequireInstall>
    </SessionProvider>
  ),
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: IndexRoute,
});

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth",
  component: SignInScreen,
});

const verifyCodeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/codice",
  component: VerifyCodeScreen,
});

const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/onboarding",
  component: () => (
    <RequireSession>
      <OnboardingScreen />
    </RequireSession>
  ),
});

const groupExpensesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/gruppi/$groupId/spese",
  component: () => (
    <RequireSession>
      <GroupExpensesScreen />
    </RequireSession>
  ),
});

const groupStatsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/gruppi/$groupId/statistiche",
  component: () => (
    <RequireSession>
      <GroupStatsScreen />
    </RequireSession>
  ),
});

const groupBalancesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/gruppi/$groupId/saldi",
  component: () => (
    <RequireSession>
      <GroupBalancesScreen />
    </RequireSession>
  ),
});

const groupSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/gruppi/$groupId/impostazioni",
  component: () => (
    <RequireSession>
      <GroupSettingsScreen />
    </RequireSession>
  ),
});

/** La modifica riusa la schermata della nuova spesa: stessi campi, stessa logica. */
const editExpenseRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/gruppi/$groupId/spese/$expenseId/modifica",
  component: function EditExpense() {
    const { expenseId } = editExpenseRoute.useParams();
    return (
      <RequireSession>
        <NewExpenseScreen expenseId={expenseId} />
      </RequireSession>
    );
  },
});

const friendsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/amici",
  component: () => (
    <RequireSession>
      <FriendsScreen />
    </RequireSession>
  ),
});

// Prima di `/amici/$key`: un percorso letterale deve battere il parametro,
// altrimenti «aggiungi» verrebbe interpretato come la chiave di una persona.
const addFriendRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/amici/aggiungi/$nickname",
  component: () => (
    <RequireSession>
      <AddFriendScreen />
    </RequireSession>
  ),
});

const friendRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/amici/$key",
  component: () => (
    <RequireSession>
      <FriendScreen />
    </RequireSession>
  ),
});

const activityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/attivita",
  component: () => (
    <RequireSession>
      <ActivityScreen />
    </RequireSession>
  ),
});

const profileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/profilo",
  component: () => (
    <RequireSession>
      <ProfileScreen />
    </RequireSession>
  ),
});

const debugRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/debug",
  component: () => (
    <RequireSession>
      <DebugScreen />
    </RequireSession>
  ),
});

/** Pubblica: si apre senza sessione, è il punto d'ingresso di un invito. */
const inviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/i/$code",
  component: InviteScreen,
});

const enterCodeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/invito",
  component: EnterCodeScreen,
});

const groupsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/gruppi",
  component: () => (
    <RequireSession>
      <GroupsScreen />
    </RequireSession>
  ),
});

const newGroupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/gruppi/nuovo",
  component: () => (
    <RequireSession>
      <NewGroupScreen />
    </RequireSession>
  ),
});

const groupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/gruppi/$groupId",
  component: () => (
    <RequireSession>
      <GroupScreen />
    </RequireSession>
  ),
});

const newExpenseRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/gruppi/$groupId/spese/nuova",
  component: () => (
    <RequireSession>
      <NewExpenseScreen />
    </RequireSession>
  ),
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  signInRoute,
  verifyCodeRoute,
  onboardingRoute,
  inviteRoute,
  enterCodeRoute,
  groupsRoute,
  newGroupRoute,
  groupRoute,
  newExpenseRoute,
  editExpenseRoute,
  groupSettingsRoute,
  groupExpensesRoute,
  groupStatsRoute,
  groupBalancesRoute,
  friendsRoute,
  addFriendRoute,
  friendRoute,
  activityRoute,
  profileRoute,
  debugRoute,
]);

export const router = createRouter({
  routeTree,
  defaultPreload: false,

  /**
   * Il router ricorda dove eri sceso.
   *
   * Casa ha centosessantacinque spese: scendere, aprirne una e tornare
   * indietro ripartendo dall'alto è la cosa che nessuna app nativa fa mai, e
   * la più fastidiosa da subire su una lista lunga.
   */
  scrollRestoration: true,

  /**
   * Le schermate si muovono invece di sostituirsi di colpo.
   *
   * Il movimento non è decorazione: dice **dove sei andato**. Senza, aprire un
   * gruppo e tornarne indietro sono la stessa identica cosa vista da fuori.
   * La direzione la decide `lib/transitions.ts`, e chi ha chiesto meno
   * animazioni non ne vede nessuna.
   */
  defaultViewTransition: true,
});

/**
 * Dopo ogni navigazione la direzione torna ad «avanti».
 *
 * Senza, un tocco su «indietro» lascerebbe l'attributo com'era e la
 * navigazione successiva scivolerebbe al contrario.
 */
resetDirectionAfterNavigation((fn) => router.subscribe("onResolved", fn));

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
